import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useSEO, getServiceSchema, getBreadcrumbSchema } from '../../../utils/seo';

export default function IsHukukuSosyalGuvenlik() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: 'İş Hukuku ve Sosyal Güvenlik Isparta Avukat Danışmanlık',
    description: 'Isparta Yıldız & Başyiğit Hukuk Bürosu iş hukuku ve sosyal güvenlik alanında iş sözleşmeleri, işçi-işveren uyuşmazlıkları, kıdem ve ihbar tazminatı, iş kazası ve meslek hastalıkları, sosyal güvenlik hakları konularında profesyonel hukuki danışmanlık hizmetleri sunmaktadır.',
    keywords: 'iş hukuku Isparta, işçi hakları, kıdem tazminatı, iş kazası, sosyal güvenlik avukatı',
    canonical: `${siteUrl}/services/is-hukuku-sosyal-guvenlik`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getServiceSchema({
          name: 'İş Hukuku ve Sosyal Güvenlik',
          description: 'İşçi-işveren ilişkileri, iş sözleşmeleri, tazminat hakları ve sosyal güvenlik konularında profesyonel hukuki danışmanlık',
          url: `${siteUrl}/services/is-hukuku-sosyal-guvenlik`
        }),
        getBreadcrumbSchema([
          { name: 'Ana Sayfa', url: siteUrl },
          { name: 'Hizmetler', url: `${siteUrl}/services` },
          { name: 'İş Hukuku ve Sosyal Güvenlik', url: `${siteUrl}/services/is-hukuku-sosyal-guvenlik` }
        ])
      ]
    }
  });

  const serviceFeatures = [
    {
      title: 'İş Sözleşmeleri',
      description: 'İş sözleşmelerinin hazırlanması, müzakeresi ve feshi',
      icon: 'ri-file-text-line'
    },
    {
      title: 'SGK İşlemleri',
      description: 'Sosyal güvenlik işlemleri ve prim borçları',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'İşçilik Alacağı Davaları',
      description: 'İşçilik alacağı ve tazminat davaları',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      title: 'Hizmet Tespit Davaları',
      description: 'Hizmet tespit ve işçi hakları davaları',
      icon: 'ri-user-heart-line'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/ee1d158d9350cd0e2bc81b2de3a6dd74.jpg')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 animate-fade-in-up">
              <Link to="/services" className="text-gray-300 hover:text-white transition-colors font-poppins font-light">
                Hizmetler
              </Link>
              <i className="ri-arrow-right-s-line mx-2 text-gray-400"></i>
              <span className="text-white font-poppins font-light">İş Hukuku ve Sosyal Güvenlik</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in-up font-poppins" style={{ animationDelay: '0.1s' }}>
              İş Hukuku ve Sosyal Güvenlik
            </h1>
            
            <p className="text-xl text-gray-200 leading-relaxed animate-fade-in-up font-poppins font-light" style={{ animationDelay: '0.2s' }}>
              İşçi ve işveren haklarının korunmasında, iş sözleşmelerinden sosyal güvenlik 
              işlemlerine kadar kapsamlı hukuki danışmanlık ve temsil hizmetleri sunuyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Service Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 animate-fade-in-up font-poppins">
              Hizmet Alanlarımız
            </h2>
            <div className="w-16 h-0.5 bg-slate-900 mx-auto mb-6"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto animate-fade-in-up font-poppins font-light" style={{ animationDelay: '0.1s' }}>
              İş hukuku ve sosyal güvenlik alanında uzman hizmetler
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {serviceFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 animate-fade-in-up hover-lift"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-slate-900 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 font-poppins">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed font-poppins font-light">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Content */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 animate-fade-in-up font-poppins">
            İş Hukuku Danışmanlığımız
          </h2>
          
          <div className="space-y-6 text-slate-600 leading-relaxed font-poppins font-light">
            <p className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              İş hukuku alanında hem işçi hem de işveren tarafında danışmanlık ve temsil hizmetleri 
              sunuyoruz. İş sözleşmelerinin hazırlanmasından fesh işlemlerine, tazminat hesaplamalarından 
              sosyal güvenlik işlemlerine kadar geniş bir yelpazede hizmet veriyoruz.
            </p>
            
            <p className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              İşçilik alacağı davaları ve hizmet tespit davalarında müvekkillerimizi temsil ediyor, 
              işçi haklarının korunması için gerekli hukuki adımları atıyoruz. İşveren müvekkillerimize 
              ise personel politikalarının oluşturulması ve iş hukuku mevzuatına uyum konularında 
              danışmanlık sağlıyoruz.
            </p>
            
            <p className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              Sosyal güvenlik işlemleri, SGK prim borçları, iş kazası tazminatları ve işsizlik 
              sigortası konularında da uzman hizmet veriyoruz. İş sağlığı ve güvenliği mevzuatına 
              uyum süreçlerinde işverenlere destek sağlıyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 animate-fade-in-up font-poppins">
            İş Hukuku Danışmanlığı
          </h2>
          <p className="text-lg text-gray-200 mb-8 animate-fade-in-up font-poppins font-light" style={{ animationDelay: '0.1s' }}>
            İş hayatındaki haklarınızı koruyun
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <button className="bg-white text-slate-900 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors font-poppins font-semibold whitespace-nowrap cursor-pointer">
              Hukuki Destek Al
            </button>
            <button className="border border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-slate-900 transition-colors font-poppins font-semibold whitespace-nowrap cursor-pointer">
              İletişime Geç
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
